/**
 * The review-request queue — the HTTP surface.
 *
 * A review request is a PROPOSAL an automation parked because it must not decide alone.
 * This file reads that queue and carries a person's decision back; it cannot create one.
 * That asymmetry is deliberate — a request comes from the automation that stopped, through
 * `api/review-requests/store.ts`, and a UI that could manufacture proposals would be a way
 * to put words in an agent's mouth that a later approval then acts on.
 *
 * ── WHY THERE IS A MAPPER AND NOT JUST A CAST ──
 *
 * `fetchApi` returns `any`, so `as Promise<T>` ASSERTS a shape rather than checking one —
 * `{ data, pagination }` versus `{ items, total }` compiles clean either way and renders an
 * EMPTY list when wrong, which no API spec can see. Two fields here break silently under a
 * cast: timestamps arrive as ISO strings (`isoOrUndefined` in api/accounting/gate.ts) and
 * are converted to ms once, here; and `payload` / `result` are opaque JSON the server has
 * already parsed out of their `_json` columns.
 */

import { apiService } from './api-service';

import { apiErrorCode } from '@/utils/api-error';
import { parseDateToMs } from '@/utils/date-utils';

/** Matches the `pathParts[1] === 'review-requests'` branch in api/index.ts. */
const BASE = '/accounting/review-requests';

/**
 * Mirrors the CHECK on `review_requests.status`, and the `STATUSES` list the API filters by.
 *
 * Exported as a VALUE as well as a type so the queue's chips and the per-status counts can
 * be derived from it — a hand-kept second list is one that goes stale the first time a
 * status is added, and goes stale by silently omitting the new one from every view.
 */
export const STATUS_VALUES = ['pending', 'approving', 'approved', 'rejected', 'failed'] as const;

export type ReviewRequestStatus = typeof STATUS_VALUES[number];

export interface ReviewRequestRow {
	id: string;
	/** Dispatch key. `executable` is false when nothing is registered to handle it. */
	kind: string;
	/** What approving would DO, from the registry — not what the requester called it. */
	actionLabel: string | null;
	/**
	 * Whether a handler is registered for this kind. False means approving can only 409, so
	 * the UI must say so rather than offering a button that cannot work.
	 */
	executable: boolean;
	/**
	 * Whether approving this kind changes anything, from the registry. `null` when the kind
	 * is unregistered (nothing to execute, so the question does not arise).
	 *
	 * The UI needs this to avoid telling a reviewer "the action ran" after approving a kind
	 * whose whole purpose is to run nothing — `escalate_for_review` is an acknowledgement,
	 * and a reviewer who believes it acted stops looking for the work still waiting for them.
	 */
	sideEffect: 'none' | 'writes' | null;
	title: string;
	summary?: string;
	reason?: string;
	payload: unknown;
	requestedByKind: string;
	requestedBy?: string;
	requestedAt: number;
	associationId?: string;
	subjectTable?: string;
	subjectId?: string;
	status: ReviewRequestStatus;
	decidedBy?: string;
	decidedAt?: number;
	decisionNote?: string;
	result: unknown;
	errorMessage?: string;
}

export interface ReviewRequestListParams {
	status?: ReviewRequestStatus;
	kind?: string;
	page?: number;
	limit?: number;
}

export interface ReviewRequestCounts {
	pending: number;
	/**
	 * Approvals that started and did not visibly finish. Surfaced apart from `pending`
	 * because pressing Approve again is the wrong move — the claim refuses it — and the row
	 * needs a person to find out what actually happened.
	 */
	inProgress: number;
	failed: number;
	/**
	 * Every status, counted across the WHOLE table rather than a loaded page. The queue's
	 * chips read these: a count taken from one page describes a subset and says so to nobody.
	 */
	byStatus: Record<ReviewRequestStatus, number>;
}

const qs = (params: Record<string, unknown>): string => {
	const sp = new URLSearchParams();
	for (const [k, v] of Object.entries(params)) {
		if (v == null || v === '') continue;
		sp.set(k, String(v));
	}
	const s = sp.toString();
	return s ? `?${s}` : '';
};

const fromWire = (raw: any): ReviewRequestRow => ({
	id: String(raw?.id ?? ''),
	kind: String(raw?.kind ?? ''),
	actionLabel: raw?.actionLabel ?? null,
	executable: raw?.executable === true,
	// Narrowed rather than cast: an older Worker that predates this field sends nothing, and
	// `undefined as 'none'` would compile clean and then claim a writing kind is a no-op.
	sideEffect: raw?.sideEffect === 'none' || raw?.sideEffect === 'writes' ? raw.sideEffect : null,
	title: String(raw?.title ?? ''),
	summary: raw?.summary ?? undefined,
	reason: raw?.reason ?? undefined,
	payload: raw?.payload ?? null,
	requestedByKind: String(raw?.requestedByKind ?? 'agent'),
	requestedBy: raw?.requestedBy ?? undefined,
	// ISO on the wire. `Number(iso)` is NaN, which renders every Requested cell as "—".
	requestedAt: parseDateToMs(raw?.requestedAt) ?? 0,
	associationId: raw?.associationId ?? undefined,
	subjectTable: raw?.subjectTable ?? undefined,
	subjectId: raw?.subjectId ?? undefined,
	status: (raw?.status ?? 'pending') as ReviewRequestStatus,
	decidedBy: raw?.decidedBy ?? undefined,
	decidedAt: raw?.decidedAt ? parseDateToMs(raw.decidedAt) ?? undefined : undefined,
	decisionNote: raw?.decisionNote ?? undefined,
	result: raw?.result ?? null,
	errorMessage: raw?.errorMessage ?? undefined,
});

/** One row of a proposed change, against the LIVE value — `GET /review-requests/:id/preview`. */
export interface ReviewPreviewRow {
	label: string;
	/** What the proposer saw (update / remove). */
	from: string | null;
	/** The value after applying; null when cleared or removed. */
	to: string | null;
	/** What is on file now. */
	live: string | null;
	/** `from` no longer matches `live`: applying would overwrite a change nobody reviewed. */
	stale: boolean;
	/** For a comma-separated email list: which addresses stay, arrive and go. */
	list?: { kept: string[]; added: string[]; removed: string[] };
	/** A create that clears `from` rather than setting a value. */
	cleared?: boolean;
}

/** One operation in a proposed change set — one card in the modal. */
export interface ReviewPreviewOp {
	index: number;
	type: string;
	kind: 'create' | 'update' | 'remove';
	title: string;
	verb: string;
	noun: string;
	count: number;
	rows: ReviewPreviewRow[];
	/** App route of the record this op targets. */
	link: string | null;
	problems: string[];
	stale: boolean;
}

export interface ReviewPreview {
	ops: ReviewPreviewOp[];
	/** Total changes — "Apply N changes". */
	count: number;
	summary: Array<{ verb: string; noun: string; count: number }>;
	stale: boolean;
	problems: string[];
	source: { label: string; path: string | null; page: number | null; fileRef: string | null } | null;
	/**
	 * Hash of everything this preview showed. Sent back with Approve; the server refuses with
	 * REVIEW_STALE if the records moved since. Null when the server sent none.
	 */
	previewToken: string | null;
}

export interface ReviewPreviewResponse {
	/** Null for a kind that changes nothing (there is nothing to preview), or an unreadable proposal. */
	preview: ReviewPreview | null;
	error?: string;
}

const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);
const strs = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);

/** Narrowed field by field: a missing `stale` read as falsy would enable Apply on a stale row. */
const previewFromWire = (raw: any): ReviewPreview | null => {
	if (!raw || typeof raw !== 'object') return null;
	const ops = (Array.isArray(raw.ops) ? raw.ops : []).map((op: any, i: number): ReviewPreviewOp => ({
		index: typeof op?.index === 'number' ? op.index : i,
		type: String(op?.type ?? ''),
		kind: op?.kind === 'create' || op?.kind === 'remove' ? op.kind : 'update',
		title: String(op?.title ?? ''),
		verb: String(op?.verb ?? 'update'),
		noun: String(op?.noun ?? 'change'),
		count: Number(op?.count ?? 1),
		rows: (Array.isArray(op?.rows) ? op.rows : []).map((r: any) => ({
			label: String(r?.label ?? ''),
			from: str(r?.from),
			to: str(r?.to),
			live: str(r?.live),
			stale: r?.stale !== false,
			...(r?.cleared === true ? { cleared: true } : {}),
			...(r?.list ? { list: { kept: strs(r.list.kept), added: strs(r.list.added), removed: strs(r.list.removed) } } : {}),
		})),
		link: str(op?.link),
		problems: strs(op?.problems),
		stale: op?.stale !== false,
	}));
	return {
		ops,
		count: Number(raw.count ?? ops.length),
		summary: (Array.isArray(raw.summary) ? raw.summary : []).map((s: any) => ({
			verb: String(s?.verb ?? ''), noun: String(s?.noun ?? ''), count: Number(s?.count ?? 0),
		})),
		stale: raw.stale !== false,
		problems: strs(raw.problems),
		previewToken: str(raw.previewToken),
		source: raw.source
			? {
				label: String(raw.source.label ?? ''),
				path: str(raw.source.path),
				page: typeof raw.source.page === 'number' ? raw.source.page : null,
				fileRef: str(raw.source.fileRef),
			}
			: null,
	};
};

export interface DecideReviewResult extends ReviewRequestRow {
	outcome: { status: 'approved' | 'rejected' | 'failed'; result?: unknown; error?: string };
}

export const reviewRequestService = {
	/**
	 * The way OUT of `approving` — record what a person found when they investigated.
	 *
	 * A request whose handler died mid-execution stays claimed: it counts toward the badge
	 * forever and no decision route will touch it. This is the only transition that can
	 * settle it, and NEITHER outcome returns it to the queue — a human already approved it,
	 * so re-offering risks running a non-idempotent handler twice.
	 *
	 * The server additionally requires the claim to be STALE: a handler that is still
	 * running and one that died look identical from outside.
	 */
	async settle(id: string, outcome: 'completed' | 'failed', note: string): Promise<DecideReviewResult> {
		const res = await apiService.fetchApi(`${BASE}/${encodeURIComponent(id)}/settle`, {
			method: 'POST',
			headers: { 'content-type': 'application/json' },
			body: JSON.stringify({ outcome, note }),
		}) as any;
		return { ...fromWire(res), outcome: res?.outcome ?? { status: 'failed', error: 'No outcome returned' } };
	},

	async list(params: ReviewRequestListParams = {}) {
		const res = await apiService.fetchApi(`${BASE}${qs(params as Record<string, unknown>)}`) as any;
		return {
			data: ((res?.data ?? []) as any[]).map(fromWire),
			pagination: res?.pagination ?? { page: 1, limit: 0, total: 0, pages: 1 },
		};
	},

	async get(id: string) {
		return fromWire(await apiService.fetchApi(`${BASE}/${encodeURIComponent(id)}`));
	},

	/** What approving would change, against live data. Read before deciding. */
	async preview(id: string): Promise<ReviewPreviewResponse> {
		const res = await apiService.fetchApi(`${BASE}/${encodeURIComponent(id)}/preview`) as any;
		return {
			preview: previewFromWire(res?.preview),
			...(typeof res?.error === 'string' ? { error: res.error } : {}),
		};
	},

	async counts(): Promise<ReviewRequestCounts> {
		const res = await apiService.fetchApi(`${BASE}/count`) as any;
		const raw = res?.byStatus ?? {};
		return {
			pending: Number(res?.pending ?? 0),
			inProgress: Number(res?.inProgress ?? 0),
			failed: Number(res?.failed ?? 0),
			// Built from the status union, not from the response's own keys — a missing status
			// becomes 0 rather than `undefined`, which a chip would render as "(NaN)".
			byStatus: Object.fromEntries(
				STATUS_VALUES.map((s) => [s, Number(raw[s] ?? 0)]),
			) as Record<ReviewRequestStatus, number>,
		};
	},

	/**
	 * Record a person's decision. Approving is the ONLY path to execution.
	 *
	 * A 200 does not mean the action succeeded — the DECISION was recorded, and a handler
	 * that threw comes back as `outcome.status === 'failed'` with the row in `failed`. The
	 * caller has to read the outcome rather than the status code, which is why this returns
	 * the row and the outcome together.
	 *
	 * `previewToken` binds an approval to the preview the approver saw: if the records moved
	 * since, the server refuses with REVIEW_STALE (see `isReviewStaleError`) and changes nothing.
	 */
	async decide(id: string, approve: boolean, note?: string, previewToken?: string | null): Promise<DecideReviewResult> {
		const res = await apiService.fetchApi(`${BASE}/${encodeURIComponent(id)}/decide`, {
			method: 'POST',
			headers: { 'content-type': 'application/json' },
			body: JSON.stringify({ approve, note: note ?? null, previewToken: previewToken ?? null }),
		}) as any;
		return { ...fromWire(res), outcome: res?.outcome ?? { status: 'failed', error: 'No outcome returned' } };
	},
};

/** Was this the server's REVIEW_STALE refusal? Recognized by the error code, not the wording. */
export const isReviewStaleError = (e: unknown): boolean => apiErrorCode(e) === 'REVIEW_STALE';
